import { useCallback, useContext, useMemo, useState } from "react";
import AuthContext from "@/contexts/AuthContext";
import { PlayersService } from "@/core/http/services/PlayersService";

const NO_COMPETITION_MESSAGE = "Jogador não está inscrito em nenhuma competição";

const normalizeUserRanking = (data) => ({
  id: data?.ranking?.id ?? data?.id ?? null,
  name: data?.ranking?.name ?? data?.name ?? "Ranking",
  year: data?.ranking?.year ?? data?.year ?? null,
  score:
    data?.points ??
    data?.score ??
    data?.ranking?.points ??
    data?.currentCategory?.ranking?.points ??
    0,
  rank:
    data?.position ??
    data?.rank ??
    data?.ranking?.position ??
    data?.currentCategory?.ranking?.position ??
    null,
  wins: data?.wins ?? 0,
  losses: data?.losses ?? 0,
  matchesPlayed: data?.matchesPlayed ?? 0,
  avatarUrl: data?.avatarUrl ?? data?.avatar ?? data?.photoUrl ?? "",
});

const useUserBase = () => {
  const { userLoggedData, updateUserFromPlayer } = useContext(AuthContext);

  const [loading, setLoading] = useState({
    position: false,
  });
  const [error, setError] = useState({ type: null, message: null });
  const [positionData, setPositionData] = useState(null);
  const [noCompetitionMessage, setNoCompetitionMessage] = useState(null);

  const getPosition = useCallback(async () => {
    const playerId = userLoggedData?.playerId;

    if (!playerId) {
      setPositionData(null);
      setNoCompetitionMessage(null);
      return;
    }

    setLoading((prev) => ({ ...prev, position: true }));
    setError({ type: null, message: null });
    setNoCompetitionMessage(null);

    try {
      const { status, data, message } =
        await PlayersService.getPosition(playerId);

      if (status === 404 && message === NO_COMPETITION_MESSAGE) {
        setPositionData(null);
        setNoCompetitionMessage(message);
        return;
      }

      if (data?.error || status !== 200) {
        setError({
          type: "position",
          message: message || "Erro ao buscar a posicao do jogador.",
        });
        return;
      }

      setPositionData(data || null);
    } catch (err) {
      setError({
        type: "position",
        message: err?.message || "Erro inesperado ao buscar a posicao do jogador.",
      });
    } finally {
      setLoading((prev) => ({ ...prev, position: false }));
    }
  }, [userLoggedData?.playerId]);

  const refreshPlayerData = useCallback(async () => {
    const playerId = userLoggedData?.playerId;
    if (!playerId) return;

    const { status, data } = await PlayersService.getById(playerId);
    if (status === 200 && data) {
      await updateUserFromPlayer(data);
    }
  }, [userLoggedData?.playerId, updateUserFromPlayer]);

  const initialize = useCallback(async () => {
    await Promise.all([getPosition()]);
  }, [getPosition]);

  const userRankings = useMemo(() => {
    if (Array.isArray(positionData)) {
      return positionData.map(normalizeUserRanking);
    }

    if (positionData) {
      return [normalizeUserRanking(positionData)];
    }

    return [];
  }, [positionData]);

  const userInfo = useMemo(() => {
    const fallbackPoints =
      userLoggedData?.currentCategory?.ranking?.points ?? 0;
    const fallbackPosition =
      userLoggedData?.currentCategory?.ranking?.position ?? null;
    const primaryRanking = userRankings[0];

    return {
      score: primaryRanking?.score ?? fallbackPoints,
      rank: `#${primaryRanking?.rank ?? fallbackPosition ?? "-"}`,
      avatarUrl: primaryRanking?.avatarUrl ?? "",
    };
  }, [userLoggedData, userRankings]);

  return {
    userLoggedData,
    userInfo,
    userRankings,
    positionData,
    noCompetitionMessage,
    loading,
    error,
    initialize,
    refreshPlayerData,
  };
};

export default useUserBase;
